import { API_BASE_URL } from '../config/api';
import { defaultAuthConfig } from '../types/auth';

export type ContactMessage = {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  createdAt?: string;
  phone?: string;
  status?: string;
};

export type SendContactMessagePayload = {
  fullname: string;
  telephone: string;
  email: string;
  subject: string;
  body: string;
};

const asRecord = (value: unknown): Record<string, unknown> => (value && typeof value === 'object' ? (value as Record<string, unknown>) : {});

const asString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
};

const firstString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    const str = asString(value);
    if (str) return str;
  }
  return undefined;
};

const normalizeMessage = (raw: unknown, fallbackIndex: number): ContactMessage => {
  const record = asRecord(raw);
  const id =
    firstString(record.id, record._id, record.contactId, record.messageId) ||
    `${firstString(record.email, record.senderEmail, record.userEmail) || 'contact'}-${fallbackIndex}`;
  const name = firstString(record.name, record.fullName, record.fullname, record.senderName) || 'Unknown';
  const email = firstString(record.email, record.senderEmail, record.userEmail) || '-';
  const subject = firstString(record.subject, record.title) || '(No subject)';
  const message = firstString(record.message, record.body, record.content, record.comment) || '';
  const createdAt = firstString(record.createdAt, record.created_at, record.timestamp, record.sentAt, record.date);
  const phone = firstString(record.phone, record.phoneNumber, record.telephone);
  const status = firstString(record.status, record.state);

  return { id, name, email, subject, message, createdAt, phone, status };
};

const buildAuthHeaders = (withJson = false): HeadersInit => {
  const headers: Record<string, string> = {
    Accept: '*/*',
  };

  if (withJson) {
    headers['Content-Type'] = 'application/json';
  }

  if (typeof window === 'undefined') {
    return headers;
  }

  const token = window.localStorage.getItem(defaultAuthConfig.tokenKey);
  const rawUser = window.localStorage.getItem(defaultAuthConfig.userKey);

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (rawUser) {
    try {
      const parsedUser = asRecord(JSON.parse(rawUser));
      const userId = asString(parsedUser.id);
      const role = asString(parsedUser.role);
      if (userId) headers['x-user-id'] = userId;
      if (role) headers['x-user-role'] = role;
    } catch {
      // ignore malformed cache
    }
  }

  return headers;
};

class ContactService {
  private baseUrl: string;

  constructor(baseUrl = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  async send(payload: SendContactMessagePayload): Promise<{ success: boolean; message: string }> {
    const fullname = payload.fullname?.trim();
    const telephone = payload.telephone?.trim();
    const email = payload.email?.trim();
    const subject = payload.subject?.trim();
    const body = payload.body?.trim();

    if (!fullname) return { success: false, message: 'Full name is required.' };
    if (!telephone) return { success: false, message: 'Telephone is required.' };
    if (!email) return { success: false, message: 'Email is required.' };
    if (!subject) return { success: false, message: 'Subject is required.' };
    if (!body) return { success: false, message: 'Message is required.' };

    const response = await fetch(`${this.baseUrl}/contact`, {
      method: 'POST',
      headers: buildAuthHeaders(true),
      body: JSON.stringify({ fullname, telephone, email, subject, body }),
    });

    let responsePayload: unknown = null;
    try {
      responsePayload = await response.json();
    } catch {
      responsePayload = null;
    }

    if (response.ok) {
      const record = asRecord(responsePayload);
      const dataRecord = asRecord(record.data);
      return {
        success: true,
        message:
          firstString(record.message, record.status, dataRecord.subject, 'Message sent.') ||
          'Message sent.',
      };
    }

    if (response.status === 404) {
      return { success: false, message: 'Contact API endpoint was not found.' };
    }

    const record = asRecord(responsePayload);
    return {
      success: false,
      message: firstString(record.error, record.message, `Request failed with status ${response.status}`) || `Request failed with status ${response.status}`,
    };
  }

  async getAll(): Promise<ContactMessage[]> {
    const response = await fetch(`${this.baseUrl}/contact`, {
      method: 'GET',
      headers: buildAuthHeaders(),
    });

    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      const record = asRecord(payload);
      const message = firstString(record.error, record.message, `Request failed with status ${response.status}`) || `Request failed with status ${response.status}`;
      throw new Error(message);
    }

    const record = asRecord(payload);
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray(record.data)
        ? record.data
        : Array.isArray(record.messages)
          ? record.messages
          : Array.isArray(record.items)
            ? record.items
            : [];

    return items.map((item, index) => normalizeMessage(item, index));
  }
}

export const contactService = new ContactService();
export { ContactService };
